/**
 * SparkSkillEffect — Lightning tree foundation active: short-range stun bolt.
 *
 * A fast, single-target electrical discharge that deals modest damage and
 * applies a brief stun. The player's first taste of stolen Conductor power —
 * functional but clearly embryonic.
 *
 * Upgrade keys consumed via UpgradeEffectResolver:
 * - `stunDurationMs` (additive, scoped to 'spark') — +250ms from Residual Charge
 * - `damageMultiplier` (multiplicative, scoped to 'spark') — ×1.2 from Residual Charge
 * - `rangeTiles` (additive, scoped to 'spark') — +0.6 from Reach
 *
 * Fizzle behavior: on failed rollSkillSuccess the discharge fizzles, but the
 * cooldown is still consumed (the attempt was made). Out-of-range attempts
 * do not consume cooldown.
 *
 * CC hierarchy: stun application checks `isImmune('stun')` before calling
 * apply. Damage always lands regardless of stun immunity.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md § Spark
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SparkDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
    /** Check CC immunity before applying stun (300ms window post-expiry). */
    isImmune(effectId: string): boolean;
  };
  /** Returns entity ID of nearest enemy within range, or null if none. */
  readonly getTarget: (rangeTiles: number) => string | null;
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct (non-DoT) hit damage to a single target. */
  readonly dealDamage: (targetId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SparkConfig {
  /** Cooldown in ms after activation or fizzle. Default: 400 (fast tier). */
  readonly cooldownMs: number;
  /** Base direct damage. Default: 7 (modest). Residual Charge multiplies. */
  readonly baseDamage: number;
  /** Base stun duration in ms. Default: 350. Residual Charge adds +250. */
  readonly baseStunDurationMs: number;
  /** Base bolt range in tiles. Default: 2. Reach adds +0.6. */
  readonly baseRangeTiles: number;
}

const DEFAULT_CONFIG: SparkConfig = {
  cooldownMs: 400,           // fast cooldown tier
  baseDamage: 7,             // modest damage — placeholder pending combat config
  baseStunDurationMs: 350,   // 350ms base stun
  baseRangeTiles: 2,         // ~2 tiles short range
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SparkSkillEffect implements SkillEffect {
  readonly skillId = 'spark';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: SparkDeps;
  private readonly config: SparkConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;

  constructor(deps: SparkDeps, config: Partial<SparkConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve range: base + Reach bonus
    const bonusRange = this.deps.resolver.getAdditiveEffect('spark', 'rangeTiles');
    const resolvedRange = this.config.baseRangeTiles + bonusRange;

    // Range check — no target in range = no cooldown consumed
    const targetId = this.deps.getTarget(resolvedRange);
    if (targetId === null) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — cooldown IS consumed even on fizzle (the discharge fired)
    if (!this.deps.rollSkillSuccess('spark')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this.deps.breakHide();

    // Resolve damage: base × Residual Charge multiplier
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect('spark', 'damageMultiplier');
    const resolvedDamage = this.config.baseDamage * damageMultiplier;

    // Deal direct damage (always lands, regardless of stun immunity)
    this.deps.dealDamage(targetId, resolvedDamage);

    // Resolve stun duration: base + Residual Charge bonus
    const bonusStunMs = this.deps.resolver.getAdditiveEffect('spark', 'stunDurationMs');
    const resolvedStunMs = this.config.baseStunDurationMs + bonusStunMs;

    // Apply stun — respect CC immunity window (300ms post-expiry)
    if (!this.deps.statusEffects.isImmune('stun')) {
      this.deps.statusEffects.apply('stun', {
        sourceSkillId: 'spark',
        durationMs: resolvedStunMs,
        intensity: 1,
        stacks: 1,
      });
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
